import React from 'react';
import { Link } from 'react-router-dom';
import { relatedRoutes, RouteMeta } from '../content/tools';
import './ToolContent.css';

interface ToolContentProps {
  route: RouteMeta;
  /**
   * Hides the related-tool links and the tools-index link. Used by the tools
   * index itself, where every link in those lists would point at a page that
   * is already on screen.
   */
  hideRelated?: boolean;
}

/**
 * The crawlable body copy for a tool page: what the tool does, when to reach
 * for it, a worked example and the FAQ. It renders the exact same copy the
 * prerender step bakes into the static HTML (src/content/tools.json), so the
 * pre-render and the hydrated app cannot drift apart.
 */
const ToolContent: React.FC<ToolContentProps> = ({ route, hideRelated = false }) => {
  const related = relatedRoutes(route);
  // Mirrors the heading the prerender step emits for the same route
  // (src/prerender/buildHtml.js), so the static and hydrated copy still match.
  const aboutHeading = route.slug === 'tools' ? 'About these tools' : 'About this tool';

  return (
    <div className="tool-content">
      <section className="tool-content__section" aria-labelledby="tool-content-about">
        <h2 className="tool-content__heading" id="tool-content-about">
          {aboutHeading}
        </h2>
        {route.intro.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </section>

      {route.whenToUse.length > 0 && (
        <section className="tool-content__section" aria-labelledby="tool-content-when">
          <h2 className="tool-content__heading" id="tool-content-when">
            When to use it
          </h2>
          <ul>
            {route.whenToUse.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="tool-content__section" aria-labelledby="tool-content-example">
        <h2 className="tool-content__heading" id="tool-content-example">
          Worked example
        </h2>
        <p className="tool-content__label">Input</p>
        <pre className="tool-content__code">
          <code>{route.example.input}</code>
        </pre>
        <p className="tool-content__label">Output</p>
        <pre className="tool-content__code">
          <code>{route.example.output}</code>
        </pre>
        {route.example.note && <p className="tool-content__note">{route.example.note}</p>}
      </section>

      {route.faq.length > 0 && (
        <section className="tool-content__section" aria-labelledby="tool-content-faq">
          <h2 className="tool-content__heading" id="tool-content-faq">
            Frequently asked questions
          </h2>
          <dl className="tool-content__faq">
            {route.faq.map((item) => (
              <React.Fragment key={item.q}>
                <dt>
                  <h3 className="tool-content__question">{item.q}</h3>
                </dt>
                <dd>{item.a}</dd>
              </React.Fragment>
            ))}
          </dl>
        </section>
      )}

      {!hideRelated && related.length > 0 && (
        <section className="tool-content__section" aria-labelledby="tool-content-related">
          <h2 className="tool-content__heading" id="tool-content-related">
            Related tools
          </h2>
          <ul className="tool-content__links">
            {related.map((tool) => (
              <li key={tool.slug}>
                <Link to={tool.path}>{tool.h1}</Link>
                <span className="tool-content__link-summary"> — {tool.shortDescription}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!hideRelated && (
        <p className="tool-content__index-link">
          <Link to="/tools">Browse all developer tools</Link>
        </p>
      )}
    </div>
  );
};

export default ToolContent;
